/**
 * User drawings, scoped per symbol.
 *
 * Keyed by symbol because a trendline drawn on MNQ means nothing on ES — a
 * single flat list would leak every instrument's lines onto every chart.
 *
 * Persisted through zustand's `persist` to localStorage rather than the
 * server-side preferences API. Drawings are high-frequency during a drag (one
 * write per mouse move) and the preferences endpoint debounces to a shared
 * blob; hammering it with geometry would fight every other preference writing
 * at the same time. localStorage is synchronous, private to the browser, and
 * correct for something a trader redraws per session.
 */
import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { Drawing, DrawingAnchor, DrawingTool } from '../components/Chart/drawingGeometry';
import { ANCHOR_COUNT } from '../components/Chart/drawingGeometry';

/** Active tool, or null when the cursor is in normal select mode. */
export type ActiveTool = DrawingTool | null;

interface DrawingState {
  /** symbol -> drawings. */
  bySymbol: Record<string, Drawing[]>;
  activeTool: ActiveTool;
  selectedId: string | null;
  /** Anchors placed so far for the in-progress drawing. */
  pending: DrawingAnchor[];

  setActiveTool: (tool: ActiveTool) => void;
  select: (id: string | null) => void;
  /** Adds an anchor; commits and returns the new drawing once the tool is satisfied. */
  addPendingAnchor: (symbol: string, anchor: DrawingAnchor) => Drawing | null;
  cancelPending: () => void;
  updateAnchors: (symbol: string, id: string, anchors: DrawingAnchor[]) => void;
  remove: (symbol: string, id: string) => void;
  clearSymbol: (symbol: string) => void;
  drawingsFor: (symbol: string) => Drawing[];
}

const newId = (): string => {
  // randomUUID is unavailable on insecure origins, which this app is served
  // over on the LAN. Falling back rather than throwing keeps drawing working
  // when the page is opened by IP instead of hostname.
  try {
    return crypto.randomUUID();
  } catch {
    return `d${Date.now().toString(36)}${Math.random().toString(36).slice(2, 8)}`;
  }
};

const EMPTY: Drawing[] = [];

export const useDrawingStore = create<DrawingState>()(
  persist(
    (set, get) => ({
      bySymbol: {},
      activeTool: null,
      selectedId: null,
      pending: [],

      setActiveTool: (tool) =>
        // Switching tools abandons a half-placed drawing: leaving the anchors
        // around would silently finish the previous shape with the new tool's
        // next click.
        set({ activeTool: tool, pending: [], selectedId: tool ? null : get().selectedId }),

      select: (id) => set({ selectedId: id }),

      addPendingAnchor: (symbol, anchor) => {
        const { activeTool, pending, bySymbol } = get();
        if (!activeTool) return null;
        const next = [...pending, anchor];
        if (next.length < ANCHOR_COUNT[activeTool]) {
          set({ pending: next });
          return null;
        }
        const drawing: Drawing = {
          id: newId(),
          tool: activeTool,
          anchors: next,
          createdAt: Date.now(),
        };
        set({
          bySymbol: { ...bySymbol, [symbol]: [...(bySymbol[symbol] ?? []), drawing] },
          pending: [],
          // Stay on the same tool so several lines can be drawn in a row, which
          // is how they actually get used.
          selectedId: drawing.id,
        });
        return drawing;
      },

      cancelPending: () => set({ pending: [] }),

      updateAnchors: (symbol, id, anchors) =>
        set((s) => ({
          bySymbol: {
            ...s.bySymbol,
            [symbol]: (s.bySymbol[symbol] ?? []).map((d) => (d.id === id ? { ...d, anchors } : d)),
          },
        })),

      remove: (symbol, id) =>
        set((s) => ({
          bySymbol: {
            ...s.bySymbol,
            [symbol]: (s.bySymbol[symbol] ?? []).filter((d) => d.id !== id),
          },
          selectedId: s.selectedId === id ? null : s.selectedId,
        })),

      clearSymbol: (symbol) =>
        set((s) => ({ bySymbol: { ...s.bySymbol, [symbol]: [] }, selectedId: null })),

      drawingsFor: (symbol) => get().bySymbol[symbol] ?? EMPTY,
    }),
    {
      name: 'chart-drawings',
      // Only the drawings survive a reload. Active tool, selection and a
      // half-placed shape are all transient interaction state — restoring a
      // pending anchor would leave the next click completing a shape the user
      // started before refreshing.
      partialize: (s) => ({ bySymbol: s.bySymbol }),
    }
  )
);
